import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { RootState } from '../../store/store';
import { updateCart, removeProductFromCart } from '../../store/slices/cartSlice';
import { productCart } from '../../types';
import displayRazorpay from '../../utils/PaymentGateway';
import Notification from '../common/Notification';
import { COLORS } from '../../utils/ColorsTheme';

const Cart = () => {
  const loadScript = (src: string) => {
    return new Promise(() => {
      const script = document.createElement('script');
      script.src = src;
      document.body.appendChild(script);
    });
  };
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: isDarkMode ? COLORS.secondaryDarkMode : COLORS.headerLightMode,
      color: isDarkMode ? theme.palette.common.white : theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));
  const isLogin = useAppSelector((state: RootState) => state.login.isLogin);
  const isUserLogin = localStorage.getItem('isLogin') === 'true' || isLogin;
  const [showMessage, setMessage] = useState<string>('');
  const cart = useAppSelector((state: RootState) => state.cart);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const updateItemToCart = (product: productCart, operation: string) => {
    const productData = { ...product };
    if (operation === 'add') {
      productData.quantity = 1;
    } else {
      productData.quantity = -1;
    }
    dispatch(updateCart(productData));
  };
  const removeFromCart = (id: number) => {
    const idObj = {
      id,
    };
    dispatch(removeProductFromCart(idObj));
  };
  const handleClose = () => {
    setMessage('');
  };
  useEffect(() => {
    loadScript('https://checkout.razorpay.com/v1/checkout.js');
  }, []);
  return (
    <div className='container main-container'>
      <ArrowBackIcon
        className='position-absolute'
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(-1)}
        data-testid='svg-back'
      />
      <Paper style={{ padding: '12px', marginLeft: '32px' }}>
        <div className='row' style={{ marginBottom: '12px' }}>
          <div className='col-lg-12'>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label='cart table'>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align='center' className='fw-bold'>
                      Product
                    </StyledTableCell>
                    <StyledTableCell align='center' className='fw-bold'>
                      Product Img
                    </StyledTableCell>
                    <StyledTableCell align='center' className='fw-bold'>
                      Price
                    </StyledTableCell>
                    <StyledTableCell align='center' className='fw-bold'>
                      Quantity
                    </StyledTableCell>
                    <StyledTableCell align='center' className='fw-bold'>
                      Total
                    </StyledTableCell>
                    <StyledTableCell align='center' className='fw-bold'>
                      Remove
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.products.map((product) => (
                    <StyledTableRow
                      key={product.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <StyledTableCell
                        component='th'
                        scope='row'
                        align='center'
                        className='cursor-pointer'
                        onClick={() =>
                          navigate(`/product-details/${product.id}`, { state: product })
                        }
                        data-testid={`product-details-${product.id}`}
                      >
                        {product.name}
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        <img src={product.imageSrc} style={{ height: 50, width: 50 }} />
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        {parseFloat(product.price).toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        <Stack
                          spacing={1}
                          direction='row'
                          sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}
                          alignItems='center'
                        >
                          <RemoveCircleOutlineIcon
                            style={product.quantity > 1 ? { cursor: 'pointer' } : {}}
                            onClick={() =>
                              product.quantity <= 1 ? null : updateItemToCart(product, 'subtract')
                            }
                            data-testid={`remove-product-${product.id}`}
                          />
                          <TextField
                            label='Quantity'
                            variant='outlined'
                            disabled
                            value={product.quantity}
                          />
                          <AddCircleOutlineIcon
                            style={product.quantity < 10 ? { cursor: 'pointer' } : {}}
                            onClick={() =>
                              product.quantity >= 10 ? null : updateItemToCart(product, 'add')
                            }
                            data-testid={`add-product-${product.id}`}
                          />
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        {product.productTotal.toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell align='center'>
                        <DeleteIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => removeFromCart(product.id)}
                          data-testid={`remove-product-from-cart-${product.id}`}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
                {cart.products.length === 0 && (
                  <caption className='text-center fw-bold'>
                    You currently have 0 product in your cart
                  </caption>
                )}
              </Table>
            </TableContainer>
          </div>
        </div>
        <div className='row'>
          <div className='col-lg-6 col-md-6'>
            <Button
              variant='contained'
              color='success'
              onClick={() => navigate('/')}
              data-testid='continue-shopping'
            >
              Continue Shopping
            </Button>
          </div>
          <div className='col-lg-4 col-md-4 offset-md-2 offset-lg-2'>
            <Typography variant='h4' component='div' sx={{ marginBottom: '12px' }}>
              Cart Total : <span>{`₹ ${cart.productsTotalPrice.toFixed(2)}`}</span>
            </Typography>
            <Button
              variant='contained'
              color='success'
              onClick={() => {
                if (!cart.products.length) {
                  setMessage('');
                  setTimeout(() => {
                    setMessage('Please Add Product to the Cart!');
                  }, 100);
                  return;
                }
                if (isUserLogin) {
                  displayRazorpay(cart, dispatch, navigate);
                } else {
                  navigate('/login');
                }
              }}
              data-testid='proceed-to-checkout'
            >
              Proceed To Checkout
            </Button>
            <div className={`form-text ${isDarkMode ? 'text-white' : 'text-dark'}`}>
              For Testing you can use card no as 5267 3181 8797 5449, Random CVV, and Any future
              Expiry date.
            </div>
          </div>
        </div>
      </Paper>
      {showMessage !== '' && (
        <Notification showMessage={showMessage} handleClose={handleClose} isSuccess={false} />
      )}
    </div>
  );
};

export default Cart;
